import type { Quest } from '../../shared/CharacterTypes';
import { getQuest } from '../../shared/content/generated/quests.gen';
import { resolveQuestEndNpcDisplay } from '../../shared/quests/questEndNpcDisplay';

export type QuestAcceptedPayload = Record<string, unknown> & {
  id?: string;
  name?: string;
};

/**
 * Idempotently project a questAccepted event into the React quest log.
 *
 * The canonical questsUpdate snapshot and the questAccepted event can arrive
 * in either order. Preserve a richer snapshot entry when it already exists;
 * otherwise append the accepted quest without mutating prior React state.
 */
export function mergeAcceptedQuest(
  activeQuests: readonly Quest[],
  acceptedQuest: QuestAcceptedPayload,
): Quest[] {
  const questId = acceptedQuest.id;
  if (!questId || activeQuests.some((quest) => quest.id === questId)) {
    return activeQuests as Quest[];
  }
  const definition = getQuest(questId);
  if (!definition) return activeQuests as Quest[];
  const firstObjective = definition.objectives[0];
  const endNpc = resolveQuestEndNpcDisplay(definition.endNpc);

  return [
    ...activeQuests,
    {
      id: questId,
      name: definition.name,
      description: definition.description,
      type: definition.type,
      endNpcId: endNpc.npcId ?? undefined,
      endNpcName: endNpc.name,
      targetId: firstObjective?.targetId ?? '',
      targetCount: firstObjective?.targetCount ?? 1,
      currentCount: 0,
      completed: false,
      objectives: definition.objectives.map(objective => ({
        type: objective.type,
        targetId: objective.targetId,
        description: objective.description,
        targetCount: objective.targetCount,
        currentCount: 0,
      })),
      // The server's questAccepted payload carries this class's rewards (lane AO).
      rewards: (acceptedQuest.rewards as Quest['rewards'] | undefined) ?? definition.rewards,
    },
  ];
}
